import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { Spinner } from '@atoms/Spinner';
import { Badge } from '@atoms/Badge';
import { Modal } from '@atoms/Modal';
import { adminService } from '@services/adminService';
import { ROLE_LABELS } from '@utils/formatters';
import type { User, UserRole } from '@/types';

export function UsersSection() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    username: '',
    name: '',
    phone: '',
    address: '',
    role: 'restaurant' as UserRole,
    password: '',
    credits: '',
  });
  const [form, setForm] = useState({
    username: '',
    password: '',
    name: '',
    role: 'restaurant' as UserRole,
    phone: '',
    address: '',
  });

  const { data: users, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminService.getUsers(),
  });

  const createMutation = useMutation({
    mutationFn: () => adminService.createUser(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setShowForm(false);
      setForm({ username: '', password: '', name: '', role: 'restaurant', phone: '', address: '' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  const creditsMutation = useMutation({
    mutationFn: ({ id, credits }: { id: string; credits: number }) =>
      adminService.addCredits(id, credits),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      setEditUser(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      adminService.updateUser(editUser!.id, {
        username: editForm.username,
        name: editForm.name,
        phone: editForm.phone,
        address: editForm.address || undefined,
        role: editForm.role,
        password: editForm.password || undefined,
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      if (editForm.credits && editUser?.role === 'wholesaler') {
        const amount = Number(editForm.credits);
        if (amount > 0) {
          await adminService.addCredits(editUser!.id, amount);
          queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
        }
      }
      setEditUser(null);
      setEditForm({ username: '', name: '', phone: '', address: '', role: 'restaurant', password: '', credits: '' });
    },
  });

  const openEdit = (u: User) => {
    setEditUser(u);
    setEditForm({
      username: u.username,
      name: u.name,
      phone: u.phone ?? '',
      address: u.address ?? '',
      role: u.role,
      password: '',
      credits: '',
    });
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kullanıcılar</h1>
          <p className="mt-1 text-sm text-slate-500">Platform kullanıcılarını yönetin</p>
        </div>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'İptal' : 'Yeni Kullanıcı'}
        </Button>
      </div>

      {showForm && (
        <Card className="grid gap-3 md:grid-cols-2">
          <Input label="Kullanıcı Adı" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          <Input label="Şifre" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <Input label="Ad" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="Adres" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <select
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
          >
            <option value="restaurant">Restoran</option>
            <option value="wholesaler">Toptancı</option>
            <option value="admin">Yönetici</option>
          </select>
          <Button loading={createMutation.isPending} onClick={() => createMutation.mutate()}>
            Oluştur
          </Button>
        </Card>
      )}

      <Modal open={Boolean(editUser)} onClose={() => setEditUser(null)} title="Kullanıcı Düzenle">
        <div className="grid gap-3">
          <Input
            label="Kullanıcı Adı"
            value={editForm.username}
            onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
          />
          <Input
            label="Ad"
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
          />
          <Input
            label="Telefon"
            value={editForm.phone}
            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
          />
          <Input
            label="Adres"
            value={editForm.address}
            onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
          />
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Rol</span>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
              value={editForm.role}
              onChange={(e) => setEditForm({ ...editForm, role: e.target.value as UserRole })}
            >
              <option value="restaurant">Restoran</option>
              <option value="wholesaler">Toptancı</option>
              <option value="admin">Yönetici</option>
            </select>
          </label>
          <Input
            label="Yeni Şifre (opsiyonel)"
            type="password"
            value={editForm.password}
            onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
          />
          {editUser?.role === 'wholesaler' && (
            <Input
              label="Reklam Kredisi Ekle"
              type="number"
              min={1}
              value={editForm.credits}
              onChange={(e) => setEditForm({ ...editForm, credits: e.target.value })}
              placeholder={`Mevcut: ${editUser.adCredits ?? 0}`}
            />
          )}
          <div className="flex gap-2">
            <Button loading={updateMutation.isPending || creditsMutation.isPending} onClick={() => updateMutation.mutate()}>
              Kaydet
            </Button>
            <Button variant="ghost" onClick={() => setEditUser(null)}>İptal</Button>
          </div>
        </div>
      </Modal>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b bg-gray-50">
            <tr>
              <th className="px-4 py-3">Ad</th>
              <th className="px-4 py-3">Kullanıcı</th>
              <th className="px-4 py-3">Rol</th>
              <th className="px-4 py-3">Kredi</th>
              <th className="px-4 py-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {(users ?? []).map((u: User) => (
              <tr key={u.id} className="border-b last:border-0">
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3">{u.username}</td>
                <td className="px-4 py-3"><Badge>{ROLE_LABELS[u.role]}</Badge></td>
                <td className="px-4 py-3">{u.adCredits ?? 0}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(u)}>
                      Düzenle
                    </Button>
                    {u.role === 'wholesaler' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => creditsMutation.mutate({ id: u.id, credits: 10 })}
                      >
                        +10 Kredi
                      </Button>
                    )}
                    {u.username !== 'admin' && (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => deleteMutation.mutate(u.id)}
                      >
                        Sil
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
